import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@mellify/ui';

export interface SidebarNavItemProps {
  /** Display label */
  label: string;
  /** Lucide icon component or any React node */
  icon: React.ReactNode;
  /** Whether this item is currently active */
  isActive?: boolean;
  /** Whether the sidebar is collapsed */
  isCollapsed?: boolean;
  /** Optional badge content (e.g., count) */
  badge?: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Optional href for anchor behavior */
  href?: string;
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  label,
  icon,
  isActive = false,
  isCollapsed = false,
  badge,
  onClick,
  href,
}) => {
  const content = (
    <>
      <span
        className={cn(
          'inline-flex items-center justify-center shrink-0 w-5 h-5 transition-colors duration-150',
          isActive
            ? 'text-primary-500'
            : 'text-dark-400 dark:text-dark-500 group-hover:text-dark-700 dark:group-hover:text-dark-200'
        )}
      >
        {icon}
      </span>
      {!isCollapsed && (
        <span
          className={cn(
            'flex-1 truncate text-sm font-medium transition-colors duration-150',
            isActive
              ? 'text-primary-700 dark:text-primary-300'
              : 'text-dark-700 dark:text-dark-300 group-hover:text-dark-900 dark:group-hover:text-dark-100'
          )}
        >
          {label}
        </span>
      )}
      {!isCollapsed && badge && (
        <span className="shrink-0">{badge}</span>
      )}
    </>
  );

  const commonClasses = cn(
    'group relative flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer transition-all duration-150 ease-default outline-none',
    'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
    isActive
      ? 'bg-primary-50 dark:bg-primary-900/20'
      : 'hover:bg-dark-100 dark:hover:bg-dark-700/50',
    isCollapsed && 'justify-center px-2'
  );

  if (href) {
    return (
      <Link
        to={href}
        className={commonClasses}
        onClick={onClick}
        aria-current={isActive ? 'page' : undefined}
        title={isCollapsed ? label : undefined}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary-500 rounded-r-full" />
        )}
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={cn(commonClasses, 'w-full text-left')}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      title={isCollapsed ? label : undefined}
    >
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary-500 rounded-r-full" />
      )}
      {content}
    </button>
  );
};

SidebarNavItem.displayName = 'SidebarNavItem';